import clsx from "clsx";
import { useSourceSystemMetadata } from "src/features/integrations/metadata";
import defaultSquare from "src/features/integrations/logos/_default.webp?url";

type Size = "small" | "large" | "xl";

type SourceSystemLogoProps = {
	logoUrl: string;
	name: string;
	className?: string | undefined;
	size?: Size | undefined;
};

// Pure presentational logo: receives resolved logoUrl + name as props. Views and
// stories render this directly; no data fetch. For "give me the logo for source
// X" use the connected SourceSystemLogoByName below.
export const SourceSystemLogo = ({
	logoUrl,
	name,
	className,
	size = "large",
}: SourceSystemLogoProps) => {
	return (
		<img
			src={logoUrl}
			onError={(e) => {
				e.currentTarget.src = defaultSquare;
			}}
			className={clsx(
				// WHY rounded-xl: logos are the one deliberately rounded tile — keep.
				"rounded-xl",
				size === "small" && "size-8",
				size === "large" && "size-10",
				size === "xl" && "size-20",
				className,
			)}
			alt={name}
			title={name}
			aria-hidden
		/>
	);
};

type SourceSystemLogoByNameProps = {
	sourceSystemName?: string | undefined;
	className?: string | undefined;
	size?: Size | undefined;
};

// Connected wrapper: resolves metadata for a source-system name, then renders the
// pure logo. Use in app/container contexts (a store is present). Do NOT use inside
// a presentational view — pass the resolved logo in as a prop instead.
export const SourceSystemLogoByName = ({
	sourceSystemName,
	className,
	size,
}: SourceSystemLogoByNameProps) => {
	const { name, logoUrl } = useSourceSystemMetadata(sourceSystemName ?? "");
	return (
		<SourceSystemLogo
			logoUrl={logoUrl}
			name={name}
			className={className}
			size={size}
		/>
	);
};
